import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSiteData } from '@/contexts/SiteDataContext';

const defaultHeroSlides = [
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/03d2b155-bb18-4322-b5ea-e28ef04c548f_compressed_WhatsApp-Image-2026-07-28-at-1.03.10-PM.webp',
    alt: 'Happy Faces Hyderabad — girls community session with volunteers',
    caption: 'Empowering Girls Across Hyderabad',
    tag: 'Education & Empowerment',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/3cce0a52-a2a7-4e74-930b-aa1a01e46561_compressed_WhatsApp-Image-2026-07-28-at-1.03.12-PM.webp',
    alt: 'Happy Faces Hyderabad — children group session with team',
    caption: 'Building Bright Futures Together',
    tag: 'Community Development',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/ffdcfd54-4b00-4116-a2e7-12ec73716629_compressed_WhatsApp-Image-2026-07-28-at-1.03.13-PM.webp',
    alt: 'Happy Faces Hyderabad Rural Development Welfare Society banner',
    caption: 'Registered & Trusted NGO Since 2026',
    tag: 'Reg. No: 4.3 of 2026',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/1c7a5be3-d015-4be9-bb55-83c43dd932f3_compressed_WhatsApp-Image-2026-07-28-at-1.03.16-PM.webp',
    alt: 'Happy Faces Hyderabad — children gathering at community event',
    caption: 'Spreading Smiles Every Day',
    tag: 'Food & Blanket Distribution',
  },
  {
    url: 'https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/fcc529ed-7139-48bb-9cb9-20d7d39555df_compressed_WhatsApp-Image-2026-07-28-at-1.03.18-PM.webp',
    alt: 'Happy Faces Hyderabad — boys school visit and engagement',
    caption: 'Inspiring the Next Generation',
    tag: 'Youth Outreach',
  },
];

const liveDonations = [
  { name: 'Rahul M.', amount: '₹500', campaign: 'Feed 500 Families', time: '2 min ago', color: 'bg-emerald-500' },
  { name: 'Ananya K.', amount: '₹1,000', campaign: 'Girls Education', time: '5 min ago', color: 'bg-amber-500' },
  { name: 'Suresh P.', amount: '₹2,500', campaign: 'Medical Help', time: '8 min ago', color: 'bg-sky-500' },
  { name: 'Priya D.', amount: '₹500', campaign: 'Winter Relief', time: '12 min ago', color: 'bg-rose-500' },
  { name: 'Vikram S.', amount: '₹5,000', campaign: 'Community Dev', time: '15 min ago', color: 'bg-violet-500' },
];

export default function HeroSection() {
  const { data } = useSiteData();
  const heroSlides = data.heroSlides.length > 0 ? data.heroSlides : defaultHeroSlides;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [donationSlide, setDonationSlide] = useState(0);
  const [prevSlideIdx, setPrevSlideIdx] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (animating || index === currentSlide) return;
    setAnimating(true);
    setPrevSlideIdx(currentSlide);
    setCurrentSlide(index);
    setTimeout(() => {
      setPrevSlideIdx(null);
      setAnimating(false);
    }, 700);
  }, [animating, currentSlide]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const t = setInterval(nextSlide, 5000);
    return () => clearInterval(t);
  }, [nextSlide]);

  useEffect(() => {
    const t = setInterval(() => setDonationSlide((p) => (p + 1) % liveDonations.length), 3500);
    return () => clearInterval(t);
  }, []);

  const getInitials = (name: string) => name.split(' ').map((n) => n[0]).join('').slice(0, 2);

  return (
    <section className="relative w-full min-h-[100vh] md:min-h-[85vh] flex flex-col overflow-hidden">

      {/* ── Full-bleed slideshow ── */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{
              opacity: i === currentSlide ? 1 : i === prevSlideIdx ? 0 : 0,
              zIndex: i === currentSlide ? 2 : i === prevSlideIdx ? 1 : 0,
            }}
          >
            <img
              src={slide.url}
              alt={slide.alt}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
        {/* Strong layered overlay for full readability */}
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/50"></div>
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>
      </div>

      {/* ── Tricolor strip ── */}
      <div className="absolute top-0 left-0 right-0 z-30 flex h-1.5">
        <div className="flex-1 bg-accent-500"></div>
        <div className="flex-1 bg-white/50"></div>
        <div className="flex-1 bg-secondary-500"></div>
      </div>

      {/* ── Main content ── */}
      <div className="relative z-20 flex-1 flex items-center w-full max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-28 pb-16 md:pt-32 md:pb-20">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ── LEFT: Text content ── */}
          <div className="text-center lg:text-left">

            {/* Slide tag badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent-400/40 bg-accent-500/15 backdrop-blur-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse flex-shrink-0"></span>
              <span className="text-accent-300 text-xs md:text-sm font-semibold tracking-wide">
                {heroSlides[currentSlide].tag}
              </span>
            </div>

            {/* Headline — fixed, always crisp */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-[4.5rem] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
              Happy Faces<br />
              <span className="text-accent-400">Hyderabad</span>
            </h1>

            <p className="text-foreground-200 text-base md:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0 mb-3 font-medium">
              Rural Development Welfare Society
            </p>
            <p className="text-white/55 text-sm md:text-base leading-relaxed max-w-lg mx-auto lg:mx-0 mb-8">
              Transforming lives through Food Distribution, Blanket Distribution, Education &amp; Community Development across Hyderabad &amp; Telangana.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start mb-10">
              <Link
                to="/donate"
                className="group inline-flex items-center gap-2.5 px-8 py-4 bg-accent-500 text-white font-bold rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-xl text-sm md:text-base"
              >
                <i className="ri-heart-fill text-lg group-hover:scale-125 transition-transform"></i>
                Donate Now
              </Link>
              <Link
                to="/volunteer"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap border border-white/25 text-sm md:text-base"
              >
                <i className="ri-team-line text-lg"></i>
                Volunteer With Us
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-8 justify-center lg:justify-start">
              <div className="text-center lg:text-left">
                <p className="text-white text-2xl font-extrabold font-heading leading-none">25,000+</p>
                <p className="text-white/50 text-xs mt-1 font-medium uppercase tracking-wider">Lives Impacted</p>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block"></div>
              <div className="text-center lg:text-left">
                <p className="text-white text-2xl font-extrabold font-heading leading-none">500K+</p>
                <p className="text-white/50 text-xs mt-1 font-medium uppercase tracking-wider">Meals Distributed</p>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block"></div>
              <div className="text-center lg:text-left">
                <p className="text-accent-400 text-2xl font-extrabold font-heading leading-none">Verified</p>
                <p className="text-white/50 text-xs mt-1 font-medium uppercase tracking-wider">Registered NGO</p>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Photo panel ── */}
          <div className="hidden lg:flex flex-col gap-4 w-full max-w-[480px] ml-auto">

            {/* Big photo preview */}
            <div className="relative w-full rounded-2xl overflow-hidden aspect-[4/3] border border-white/15">
              {heroSlides.map((slide, i) => (
                <img
                  key={i}
                  src={slide.url}
                  alt={slide.alt}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: i === currentSlide ? 1 : 0 }}
                />
              ))}

              {/* Slide caption bar at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/50 to-transparent px-5 py-4 z-10">
                <p className="text-white text-sm font-bold leading-snug">{heroSlides[currentSlide].caption}</p>
                <p className="text-accent-300 text-xs mt-0.5 font-medium">{heroSlides[currentSlide].tag}</p>
              </div>

              {/* Slide counter badge */}
              <div className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-foreground-950/70 backdrop-blur-sm border border-white/15">
                <span className="text-white text-xs font-semibold">{currentSlide + 1} / {heroSlides.length}</span>
              </div>

              {/* Prev/Next arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/50 hover:bg-foreground-950/80 text-white transition-all duration-200 backdrop-blur-sm border border-white/15"
                aria-label="Previous slide"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/50 hover:bg-foreground-950/80 text-white transition-all duration-200 backdrop-blur-sm border border-white/15"
                aria-label="Next slide"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>

            {/* Thumbnails row */}
            <div className="grid grid-cols-5 gap-2">
              {heroSlides.map((slide, i) => (
                <button
                  key={i}
                  onClick={() => goToSlide(i)}
                  className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer transition-all duration-300 ${
                    i === currentSlide
                      ? 'ring-2 ring-accent-400 ring-offset-1 ring-offset-transparent'
                      : 'opacity-45 hover:opacity-75 hover:ring-1 hover:ring-white/30'
                  }`}
                  aria-label={`Slide ${i + 1}: ${slide.caption}`}
                >
                  <img src={slide.url} alt={slide.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Live donations card */}
            <div className="w-full bg-foreground-950/60 backdrop-blur-md rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                  </span>
                  <span className="text-white/90 text-xs font-bold tracking-wide">Live Donations</span>
                </div>
                <span className="text-white/35 text-[10px] font-medium">Real-time</span>
              </div>
              <div className="relative h-14 overflow-hidden">
                {liveDonations.map((donation, idx) => (
                  <div
                    key={idx}
                    className="absolute inset-0 flex items-center justify-between transition-all duration-700 ease-in-out"
                    style={{
                      opacity: idx === donationSlide ? 1 : 0,
                      transform: idx === donationSlide ? 'translateY(0)' : 'translateY(8px)',
                      pointerEvents: idx === donationSlide ? 'auto' : 'none',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full ${donation.color} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                        {getInitials(donation.name)}
                      </div>
                      <div>
                        <p className="text-white text-sm font-semibold leading-none">{donation.name}</p>
                        <p className="text-white/45 text-[11px] mt-0.5">{donation.campaign}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-accent-300 text-base font-extrabold leading-none">{donation.amount}</p>
                      <p className="text-white/35 text-[10px] mt-0.5">{donation.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom slide dot indicators ── */}
      <div className="relative z-20 flex items-center justify-center gap-2 pb-6">
        {heroSlides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
              idx === currentSlide ? 'w-8 bg-accent-400' : 'w-2 bg-white/25 hover:bg-white/50'
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* ── Mobile prev/next arrows ── */}
      <button
        onClick={prevSlide}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-950/50 hover:bg-foreground-950/80 text-white transition-all duration-200 backdrop-blur-sm border border-white/15 lg:hidden"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-950/50 hover:bg-foreground-950/80 text-white transition-all duration-200 backdrop-blur-sm border border-white/15 lg:hidden"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}